<template>
  <header
    class="fixed inset-x-0 top-0 z-40 transition-all duration-300"
    :class="menuOpen ? 'border-b border-gray-200/60 bg-white dark:border-white/10 dark:bg-primary-dark' : scrolled ? 'border-b border-gray-200/60 bg-white/80 shadow-soft backdrop-blur-lg dark:border-white/10 dark:bg-primary-dark/80' : 'border-b border-transparent'"
  >
    <nav class="container mx-auto flex h-16 items-center justify-between sm:h-20" aria-label="Main">
      <NuxtLink to="/" aria-label="Vedware home" class="shrink-0">
        <LogoHorizontalIcon class="h-12 w-[8.4rem] text-ternary-dark dark:text-white sm:h-14 sm:w-[9.8rem]" />
      </NuxtLink>

      <!-- Desktop links -->
      <div class="hidden items-center gap-1 lg:flex">
        <template v-for="link in mainLinks" :key="link.to">
          <div v-if="link.to === '/services'" class="group relative">
            <NuxtLink :to="link.to" class="nav-link inline-flex items-center gap-1">
              {{ link.label }}
              <PhCaretDown class="size-3.5 transition-transform group-focus-within:rotate-180 group-hover:rotate-180" />
            </NuxtLink>
            <div class="invisible absolute left-0 top-full z-10 w-64 pt-2 opacity-0 transition-all duration-200 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
              <div class="rounded-2xl border border-gray-200/60 bg-white p-2 shadow-soft-lg dark:border-white/10 dark:bg-secondary-dark">
                <NuxtLink
                  v-for="service in serviceLinks"
                  :key="service.to"
                  :to="service.to"
                  class="block rounded-xl px-4 py-2.5 text-sm font-medium text-ternary-dark transition-colors hover:bg-accent/10 hover:text-accent dark:text-ternary-light dark:hover:text-accent"
                >
                  {{ service.label }}
                </NuxtLink>
              </div>
            </div>
          </div>
          <NuxtLink v-else :to="link.to" class="nav-link">
            {{ link.label }}
          </NuxtLink>
        </template>
      </div>

      <div class="flex items-center gap-3">
        <Btn class="hidden sm:inline-flex" aria-label="Hire Us" @click="showModal = true">
          Hire Us
        </Btn>
        <ThemeToggle />
        <button
          type="button"
          class="rounded-lg p-2 text-ternary-dark dark:text-ternary-light lg:hidden"
          :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
          :aria-expanded="menuOpen"
          aria-controls="mobile-menu"
          @click="menuOpen = !menuOpen"
        >
          <PhX v-if="menuOpen" class="size-7" />
          <PhList v-else class="size-7" />
        </button>
      </div>
    </nav>

    <!-- Mobile menu -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      leave-active-class="transition duration-150 ease-in"
      enter-from-class="-translate-y-2 opacity-0"
      leave-to-class="-translate-y-2 opacity-0"
    >
      <div
        v-if="menuOpen"
        id="mobile-menu"
        class="h-[calc(100dvh-4rem)] overflow-y-auto px-6 pb-8 pt-2 lg:hidden"
      >
        <NuxtLink v-for="link in mainLinks" :key="link.to" :to="link.to" class="mobile-link">
          {{ link.label }}
        </NuxtLink>
        <div class="mt-2 border-t border-gray-200 pt-4 dark:border-white/10">
          <p class="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Services
          </p>
          <NuxtLink v-for="service in serviceLinks" :key="service.to" :to="service.to" class="block py-2.5 text-gray-600 dark:text-gray-300">
            {{ service.label }}
          </NuxtLink>
        </div>
        <Btn class="mt-6 w-full justify-center" aria-label="Hire Us" @click="openHire">
          Hire Us
        </Btn>
      </div>
    </Transition>

    <HireMeModal v-model="showModal" />
  </header>
</template>

<script setup lang="ts">
import { PhCaretDown, PhList, PhX } from '@phosphor-icons/vue'
import HireMeModal from '~/components/HireMeModal.vue'
import LogoHorizontalIcon from '~/components/Icons/LogoHorizontalIcon.vue'
import Btn from '~/components/UI/Btn.vue'
import ThemeToggle from '~/components/UI/ThemeToggle.vue'
import { mainLinks, serviceLinks } from '~/data/nav'

const route = useRoute()
const menuOpen = ref(false)
const showModal = ref(false)
const scrolled = ref(false)

function onScroll () {
  scrolled.value = window.scrollY > 8
}

function onKeydown (event: KeyboardEvent) {
  if (event.key === 'Escape') { menuOpen.value = false }
}

function openHire () {
  menuOpen.value = false
  showModal.value = true
}

watch(() => route.fullPath, () => { menuOpen.value = false })
watch(menuOpen, open => document.documentElement.classList.toggle('overflow-hidden', open))

onMounted(() => {
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('keydown', onKeydown)
  document.documentElement.classList.remove('overflow-hidden')
})
</script>

<style scoped>
.nav-link {
  @apply rounded-lg px-4 py-2 text-base font-medium text-primary-dark transition-colors hover:text-accent dark:text-ternary-light dark:hover:text-accent;
}

.nav-link.router-link-active {
  @apply text-accent dark:text-accent;
}

.mobile-link {
  @apply block border-b border-gray-200/60 py-3.5 text-lg font-medium text-primary-dark dark:border-white/10 dark:text-ternary-light;
}

.mobile-link.router-link-active {
  @apply text-accent dark:text-accent;
}
</style>
